import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

interface ArchivedClient {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  isDeleted: boolean;
  offboarded: boolean;
  offboardingDate?: string;
  offboardingReason?: string;
}

const ArchivedClients: React.FC = () => {
  const [clients, setClients] = useState<ArchivedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'deleted' | 'retained'>('all');
  const navigate = useNavigate();

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    fetchArchivedClients();
  }, []);

  const fetchArchivedClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/clients/archived');
      // Ensure response.data is an array
      const clientData = Array.isArray(response.data) ? response.data : [];
      console.log('Archived clients response:', response.data); // Debug log
      setClients(clientData);
    } catch (err: any) {
      console.error('Error fetching archived clients:', err);
      const errorMessage = err?.response?.data?.error || 'Failed to fetch archived clients';
      setError(errorMessage);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    // Ensure clients is an array before filtering
    const clientsArray = Array.isArray(clients) ? clients : [];
    
    return clientsArray.filter(client => {
      if (!client) return false;
      
      const matchesSearch = 
        (client.firstName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (client.lastName?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (client.email?.toLowerCase() || '').includes(searchQuery.toLowerCase());

      if (filterType === 'all') return matchesSearch;
      if (filterType === 'deleted') return matchesSearch && client.isDeleted;
      return matchesSearch && !client.isDeleted;
    });
  }, [clients, searchQuery, filterType]);

  // Get paginated data
  const paginatedClients = filteredClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleFilterChange = (event: SelectChangeEvent<'all' | 'deleted' | 'retained'>) => {
    setFilterType(event.target.value as 'all' | 'deleted' | 'retained');
    setPage(0); // Reset to first page when filter changes
  };

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          label="Search clients"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0); // Reset to first page when search changes
          }}
          sx={{ 
            minWidth: 200,
            '& .MuiInputLabel-root': {
              color: 'rgba(255, 255, 255, 0.7)'
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'rgba(255, 255, 255, 0.9)'
            },
            '& .MuiOutlinedInput-input': {
              color: 'white'
            }
          }}
        />
        <FormControl size="small" sx={{ 
          minWidth: 150,
          '& .MuiInputLabel-root': {
            color: 'rgba(255, 255, 255, 0.7)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'rgba(255, 255, 255, 0.9)'
          },
          '& .MuiSelect-select': {
            color: 'white'
          }
        }}>
          <InputLabel>Filter</InputLabel>
          <Select
            value={filterType}
            label="Filter"
            onChange={handleFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="deleted">Deleted</MenuItem>
            <MenuItem value="retained">Retained</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <div className="overflow-x-auto glass-card rounded-lg">
        <table className="min-w-full">
          <thead className="glass-card-light">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {paginatedClients.map((client) => (
              <tr key={client._id} className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium theme-text-primary">
                    {client.firstName} {client.lastName}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm theme-text-secondary">
                    {client.email || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm theme-text-secondary">
                    {client.phone || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm">
                    <Chip
                      label={client.isDeleted ? 'Deleted' : 'Retained'}
                      color={client.isDeleted ? 'error' : 'success'}
                      size="small"
                    />
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/client-details/${client._id}`);
                    }}
                    className="text-blue-400 font-bold hover:text-blue-300 transition-colors"
                  >
                    View Details
                  </button>
                  {client.isDeleted && (
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/api/clients/${client._id}/reactivate`);
                          fetchArchivedClients();
                        } catch (err) {
                          console.error('Error reactivating client:', err);
                          setError('Failed to reactivate client');
                        }
                      }}
                    >
                      Reactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
            className={`relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
              page === 0
                ? 'bg-white/5 text-white/40'
                : 'bg-white/10 theme-text-primary hover:bg-white/20'
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= Math.ceil(filteredClients.length / rowsPerPage) - 1}
            className={`ml-3 relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
              page >= Math.ceil(filteredClients.length / rowsPerPage) - 1
                ? 'bg-white/5 text-white/40'
                : 'bg-white/10 theme-text-primary hover:bg-white/20'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm theme-text-secondary">
              {filteredClients.length === 0 
                ? 'No results' 
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, filteredClients.length)} of ${filteredClients.length} results`
              }
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => handleChangePage(page - 1)}
                disabled={page === 0}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-white/20 bg-white/10 text-sm font-medium ${
                  page === 0
                    ? 'text-white/40'
                    : 'theme-text-primary hover:bg-white/20'
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => handleChangePage(page + 1)}
                disabled={page >= Math.ceil(filteredClients.length / rowsPerPage) - 1}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-white/20 bg-white/10 text-sm font-medium ${
                  page >= Math.ceil(filteredClients.length / rowsPerPage) - 1
                    ? 'text-white/40'
                    : 'theme-text-primary hover:bg-white/20'
                }`}
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default ArchivedClients;
